import { useId } from 'react';

import { wacToken } from '@/libraries/design/foundations';

import { VIEWBOX_SCALE } from './spiderweb-utils';

const SHIMMER_CONCURRENCY_RATIO = 0.2;
const SHIMMER_SLOT_DURATION_SEC = 3;
const SHIMMER_TRAVEL_DURATION_SEC = 1;
const SHIMMER_INITIAL_DELAY_SEC = 1;
const SHIMMER_LANE_OFFSET_SEC = 0.35;
const SHIMMER_PATH_LENGTH = 100;
const SHIMMER_PACKET_LENGTH = 18;

type SpiderWebConnectorShimmerProps = {
	d: string;
	connectorId: string;
	strokeWidth: number;
	viewBoxHeight: number;
	strokeDasharray?: string;
	startDelaySec: number;
	shimmerIndex: number;
	shimmerCount: number;
};

export type SpiderWebConnectorShimmerTiming = {
	beginSec: number;
	cycleDurationSec: number;
	travelFraction: number;
};

export function getMaxSimultaneousSpiderWebShimmers(shimmerCount: number): number {
	return Math.max(1, Math.round(Math.max(0, shimmerCount) * SHIMMER_CONCURRENCY_RATIO));
}

export function getSpiderWebConnectorShimmerTiming(
	shimmerIndex: number,
	shimmerCount: number,
): SpiderWebConnectorShimmerTiming {
	const safeCount = Math.max(1, shimmerCount);
	const safeIndex = Math.min(Math.max(0, shimmerIndex), safeCount - 1);
	const simultaneousShimmers = getMaxSimultaneousSpiderWebShimmers(safeCount);
	const slotCount = Math.ceil(safeCount / simultaneousShimmers);
	const slotIndex = Math.floor(safeIndex / simultaneousShimmers);
	const laneIndex = safeIndex % simultaneousShimmers;
	const cycleDurationSec = slotCount * SHIMMER_SLOT_DURATION_SEC;

	return {
		beginSec:
			SHIMMER_INITIAL_DELAY_SEC +
			slotIndex * SHIMMER_SLOT_DURATION_SEC +
			laneIndex * SHIMMER_LANE_OFFSET_SEC,
		cycleDurationSec,
		travelFraction: SHIMMER_TRAVEL_DURATION_SEC / cycleDurationSec,
	};
}

function toSvgId(value: string): string {
	return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}

export const SpiderWebConnectorShimmer = ({
	d,
	connectorId,
	strokeWidth,
	viewBoxHeight,
	strokeDasharray,
	startDelaySec,
	shimmerIndex,
	shimmerCount,
}: SpiderWebConnectorShimmerProps) => {
	const instanceId = toSvgId(useId());
	const safeId = `${instanceId}-${toSvgId(connectorId)}`;
	const maskId = `connector-shimmer-mask-${safeId}`;
	const blurId = `connector-shimmer-blur-${safeId}`;
	const timing = getSpiderWebConnectorShimmerTiming(shimmerIndex, shimmerCount);
	const fadeFraction = Math.min(0.04, timing.travelFraction / 4);
	const visibleEndFraction = Math.max(fadeFraction, timing.travelFraction - fadeFraction);
	const packetStartOffset = SHIMMER_PATH_LENGTH + SHIMMER_PACKET_LENGTH;
	const packetEndOffset = -SHIMMER_PACKET_LENGTH;
	const maskPadding = 100;
	const animationBeginSec = startDelaySec + timing.beginSec;

	return (
		<>
			<defs>
				<filter
					id={blurId}
					filterUnits="userSpaceOnUse"
					x={-maskPadding}
					y={-maskPadding}
					width={VIEWBOX_SCALE + maskPadding * 2}
					height={viewBoxHeight + maskPadding * 2}
				>
					<feGaussianBlur stdDeviation="5" />
				</filter>
				<mask
					id={maskId}
					maskUnits="userSpaceOnUse"
					x={-maskPadding}
					y={-maskPadding}
					width={VIEWBOX_SCALE + maskPadding * 2}
					height={viewBoxHeight + maskPadding * 2}
				>
					<path
						d={d}
						pathLength={SHIMMER_PATH_LENGTH}
						fill="none"
						stroke={wacToken('color.neutral.light0')}
						strokeWidth={Math.max(strokeWidth * 5, 10)}
						strokeLinecap="round"
						strokeDasharray={`${SHIMMER_PACKET_LENGTH} ${SHIMMER_PATH_LENGTH}`}
						strokeDashoffset={packetStartOffset}
						opacity={0}
						filter={`url(#${blurId})`}
					>
						<animate
							attributeName="stroke-dashoffset"
							values={`${packetStartOffset};${packetEndOffset};${packetEndOffset}`}
							keyTimes={`0;${timing.travelFraction};1`}
							dur={`${timing.cycleDurationSec}s`}
							begin={`${animationBeginSec}s`}
							repeatCount="indefinite"
						/>
						<animate
							attributeName="opacity"
							values="0;1;1;0;0"
							keyTimes={`0;${fadeFraction};${visibleEndFraction};${timing.travelFraction};1`}
							dur={`${timing.cycleDurationSec}s`}
							begin={`${animationBeginSec}s`}
							repeatCount="indefinite"
						/>
					</path>
				</mask>
			</defs>
			<path
				className="connector-shimmer-overlay"
				d={d}
				fill="none"
				stroke={wacToken('color.neutral.light0')}
				strokeWidth={strokeWidth}
				strokeDasharray={strokeDasharray}
				strokeLinecap="round"
				mask={`url(#${maskId})`}
				vectorEffect="non-scaling-stroke"
				pointerEvents="none"
			/>
		</>
	);
};
